import {
  ChannelIcon,
  Flex,
  Heading,
  HoverCard,
  InfoIcon,
  Paragraph,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { getChannelName } from '~publish/helpers/channels'

import type { EngagementType } from '../../graphqlEnums'

import { getNoMediaCopy } from './noMediaCopy'
import type { NoMediaChannel, NoMediaExplanation } from './noMediaCopy'

import styles from './NoMediaHint.module.css'

import type { JSX } from 'react'

type NoMediaHintProps = {
  /**
   * The channels in scope that can't surface media for this engagement type.
   * Empty renders nothing, so callers never branch. See `useNoMediaChannels`.
   */
  noMediaChannels: NoMediaChannel[]
  engagementType: EngagementType
}

/**
 * The affected platforms, shown as logos in place of their names. Uses the same
 * subtle grey treatment as the by-post summary bar's "Includes replies via …",
 * sized up to `small` because this sentence is body text rather than the bar's
 * smaller label.
 *
 * The logos stay decorative (Popcorn icons are `aria-hidden`) and the same list
 * is announced as text, so the sentence doesn't open with nothing at all for
 * screen-reader users.
 *
 * @see Comments/CommentsByPost/CommentsForPost/CommentsSummaryBar.tsx
 */
const PlatformLogos = ({
  services,
}: {
  services: NoMediaChannel['service'][]
}): JSX.Element => (
  <>
    <VisuallyHidden>{services.map(getChannelName).join(', ')}</VisuallyHidden>
    <span className={styles.logos}>
      {services.map((service) => (
        <ChannelIcon
          key={service}
          type={service}
          size="small"
          color="subtle"
          data-testid="platform-logo"
        />
      ))}
    </span>
  </>
)

const Explanation = ({
  explanation,
}: {
  explanation: NoMediaExplanation
}): JSX.Element => {
  if (explanation.kind === 'composed') {
    return (
      <Paragraph size="md">
        <PlatformLogos services={explanation.services} /> {explanation.body}
      </Paragraph>
    )
  }

  return (
    <>
      {explanation.paragraphs.map((paragraph) => (
        <Paragraph key={paragraph} size="md">
          {paragraph}
        </Paragraph>
      ))}
    </>
  )
}

/**
 * Inline hint above the inbox list explaining that items here arrive without
 * their images or videos, with a hover card carrying the detail.
 *
 * The icon alone is the trigger — hovering the hint text does nothing. It's an
 * `UnstyledButton` so it is keyboard-focusable and the card opens on focus as
 * well as hover; since it carries no visible text of its own, its accessible
 * name is the card's title.
 *
 * @see getNoMediaCopy for which copy is backend-authored and which isn't.
 */
export const NoMediaHint = ({
  noMediaChannels,
  engagementType,
}: NoMediaHintProps): JSX.Element | null => {
  const copy = getNoMediaCopy(noMediaChannels, engagementType)

  if (!copy) {
    return null
  }

  return (
    <div className={styles.container}>
      <Flex className={styles.row} align="center" gap="2xs">
        <HoverCard openDelay="fast" closeDelay="fast">
          <HoverCard.Trigger>
            <UnstyledButton className={styles.trigger} aria-label={copy.title}>
              <InfoIcon color="subtle" />
            </UnstyledButton>
          </HoverCard.Trigger>
          <HoverCard.Content
            side="bottom"
            align="start"
            className={styles.content}
          >
            <Flex direction="column" gap="xs">
              <Heading size="sm">{copy.title}</Heading>
              <Explanation explanation={copy.explanation} />
            </Flex>
          </HoverCard.Content>
        </HoverCard>
        <Text size="sm" color="subtle">
          {copy.hint}
        </Text>
      </Flex>
    </div>
  )
}

NoMediaHint.displayName = 'NoMediaHint'
